/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { getProp } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import MenuController from "@monsma/MobiusOverrides/Menu/MenuController";
import { screen } from "@monsma/XDL/Helpers";
import React from "react";
import styled, { css, withTheme } from "styled-components";

export type XPopupProps = MobiusStyledComponentProps<
    "div",
    {
        trigger: React.ReactElement<{ className: string }>;
        shade?: boolean;
        css?: StyledProp;
    }
>;

export const XPopupWrapper = styled.div<InjectableCss>`
    position: relative;

    & > div {
        position: absolute;
        background: white;
        top: 100%;
        right: 0;
        box-shadow: ${getProp("theme.shadows.2")};
        padding: 30px 20px 25px;
        z-index: ${getProp("theme.zIndex.xPopup")};
        transition: all 300ms ease;
        opacity: 0;
        pointer-events: none;

        form {
            display: none;
        }

        ${injectCss}
    }

    &.active > div {
        opacity: 1;
        pointer-events: all;

        form {
            display: block;
        }
    }

    * {
        pointer-events: none;
    }

    &.active * {
        pointer-events: all;
    }

    & > *:first-child,
    & > *:first-child * {
        pointer-events: all;
    }

    ${screen("lte", "md")} {
        & > div {
            display: none;
        }
    }
`;

export interface XPopupStyles {
    wrapper?: InjectableCss;
}

export const notifyStyles: XPopupStyles = {
    wrapper: {
        css: css``,
    },
};

const XPopup: React.FC<XPopupProps> = ({ children, trigger, shade = false, ...otherProps }) => {
    const openEvent = (e: any) => {
        if (
            e.target !== e.currentTarget.querySelector(":scope > button") &&
            e.target !== e.currentTarget.querySelector(":scope > .xPopupBody")
        ) {
            return;
        }
        const elm = e.currentTarget;

        MenuController.registerOpen(elm, (current: HTMLElement) => {
            document.querySelector("body")?.classList?.[shade ? "add" : "remove"]("shade");
        });
    };

    const closeEvent = (e: any, timeout: number = MenuController.closeTime) => {
        const elm = e.currentTarget;

        MenuController.registerClose(
            elm,
            (current: HTMLElement) => {
                shade && document.querySelector("body")?.classList?.remove("shade");
            },
            timeout,
        );
    };

    return (
        <XPopupWrapper
            onMouseOver={openEvent}
            onMouseLeave={closeEvent}
            onClick={(e: any) => {
                // check if user clicked on the nav trigger
                if (
                    e.currentTarget?.querySelector(":scope > .active")?.contains(e.target) ||
                    e.currentTarget === e.target
                ) {
                    closeEvent(e, 20);
                }
            }}
            {...otherProps}
        >
            {React.cloneElement(trigger, { className: "trigger" })}
            <div className="xPopupBody">{children}</div>
        </XPopupWrapper>
    );
};

export default withTheme(XPopup);
